import React, { Component } from "react";
import {
  Card,
  CardBody,
  Row,
  Button,
  Input,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { Colxx, Separator } from "../../../components/common/CustomBootstrap";
import Breadcrumb from "../../../containers/navs/Breadcrumb";
import { END_POINT } from "../../../constants/defaultValues";
import axios from "axios";
import ShopeeV2Api from "../../../services/shopeeV2";

export const SHOPEE_MAPPING_PAGE_SIZE = 20;
export const SHOPEE_MAPPING_SEARCH_DELAY_MS = 300;

export const normalizeShopeeMappingMeta = (meta, page = 1) => {
  const total = Number.parseInt(meta && meta.total, 10);
  const totalPages = Number.parseInt(meta && meta.total_pages, 10);
  const resolvedPage = Number.parseInt(meta && meta.page, 10);
  return {
    page: Number.isFinite(resolvedPage) ? Math.max(1, resolvedPage) : page,
    page_size: SHOPEE_MAPPING_PAGE_SIZE,
    total: Number.isFinite(total) ? Math.max(0, total) : 0,
    total_pages: Number.isFinite(totalPages) ? Math.max(1, totalPages) : 1,
  };
};

const isCanceledRequest = (error) =>
  error &&
  (error.name === "CanceledError" ||
    error.name === "AbortError" ||
    error.code === "ERR_CANCELED" ||
    error.message === "canceled");

export class EcommerceProducts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: "1",
      shopeeMappings: [],
      shopeeMeta: normalizeShopeeMappingMeta(null),
      tiktokMappings: [],
      loadingShopee: true,
      loadingTiktok: true,
      shopeeError: "",
      searchQuery: "",
    };
    this.shopeeAbortController = null;
    this.shopeeSearchTimer = null;
  }

  componentDidMount() {
    this.loadMappings();
  }

  componentWillUnmount() {
    if (this.shopeeAbortController) this.shopeeAbortController.abort();
    if (this.shopeeSearchTimer) clearTimeout(this.shopeeSearchTimer);
  }

  loadShopeeMappings = async ({
    page = this.state.shopeeMeta.page,
    search = this.state.searchQuery,
  } = {}) => {
    if (this.shopeeAbortController) this.shopeeAbortController.abort();
    const controller = new AbortController();
    this.shopeeAbortController = controller;
    this.setState({ loadingShopee: true, shopeeError: "" });

    try {
      const response = await ShopeeV2Api.mappings(
        "ALL",
        {
          page,
          page_size: SHOPEE_MAPPING_PAGE_SIZE,
          search: String(search || "").trim() || undefined,
        },
        controller.signal,
      );
      if (controller.signal.aborted) return;
      this.setState({
        shopeeMappings: response.data || [],
        shopeeMeta: normalizeShopeeMappingMeta(response.meta, page),
        loadingShopee: false,
      });
    } catch (err) {
      if (!controller.signal.aborted && !isCanceledRequest(err)) {
        console.error("Error loading Shopee mappings:", err);
        this.setState({
          loadingShopee: false,
          shopeeError: "Không thể tải sản phẩm Shopee",
        });
      }
    } finally {
      if (this.shopeeAbortController === controller) {
        this.shopeeAbortController = null;
      }
    }
  };

  loadTiktokMappings = async () => {
    try {
      const user_id = localStorage.getItem("b");
      const response = await axios
        .get(`${END_POINT}/api/tiktok/mappings?user_id=${user_id}`)
        .catch(() => ({ data: { data: [] } }));
      this.setState({
        tiktokMappings: response.data.data || [],
        loadingTiktok: false,
      });
    } catch (err) {
      console.error("Error loading TikTok mappings:", err);
      this.setState({ loadingTiktok: false });
    }
  };

  loadMappings = () =>
    Promise.all([this.loadShopeeMappings(), this.loadTiktokMappings()]);

  handleSearchChange = (event) => {
    const searchQuery = event.target.value;
    if (this.shopeeAbortController) this.shopeeAbortController.abort();
    if (this.shopeeSearchTimer) clearTimeout(this.shopeeSearchTimer);
    this.setState(
      (current) => ({
        searchQuery,
        shopeeMeta: { ...current.shopeeMeta, page: 1 },
      }),
      () => {
        this.shopeeSearchTimer = setTimeout(() => {
          this.shopeeSearchTimer = null;
          this.loadShopeeMappings({ page: 1, search: this.state.searchQuery });
        }, SHOPEE_MAPPING_SEARCH_DELAY_MS);
      },
    );
  };

  changeShopeePage = (page) => {
    const { total_pages } = this.state.shopeeMeta;
    if (page < 1 || page > total_pages || page === this.state.shopeeMeta.page) {
      return;
    }
    this.loadShopeeMappings({ page, search: this.state.searchQuery });
  };

  toggleTab = (tab) => {
    if (this.state.activeTab !== tab) {
      this.setState({ activeTab: tab });
    }
  };

  formatPrice = (price) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(price || 0);
  };

  filterMappings = (mappings) => {
    const { searchQuery } = this.state;
    if (!searchQuery) return mappings;
    return mappings.filter(
      (m) =>
        (m.platform_name || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        (m.platform_sku || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
    );
  };

  renderMappingCard = (mapping, platform) => {
    return (
      <div
        key={mapping.id}
        className="d-flex align-items-center p-3 mb-2"
        style={{
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #dee2e6",
        }}
      >
        <div
          style={{
            width: 60,
            height: 60,
            backgroundColor: "#e9ecef",
            borderRadius: "8px",
            overflow: "hidden",
            marginRight: 16,
            flexShrink: 0,
          }}
        >
          {mapping.platform_image ? (
            <img
              src={mapping.platform_image}
              alt={mapping.platform_name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <div
              className="d-flex align-items-center justify-content-center h-100"
              style={{ color: "#adb5bd" }}
            >
              <i className="simple-icon-picture" style={{ fontSize: 24 }} />
            </div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="d-flex justify-content-between align-items-start">
            <div style={{ flex: 1 }}>
              <h6 className="mb-1 text-truncate" style={{ maxWidth: "300px" }}>
                {mapping.platform_name || "Chưa có tên"}
              </h6>
              <div className="text-muted" style={{ fontSize: "13px" }}>
                SKU: <code>{mapping.platform_sku || "N/A"}</code>
              </div>
            </div>
            <div className="text-right">
              <div className="font-weight-bold text-primary">
                {this.formatPrice(mapping.platform_price)}
              </div>
              <Badge
                color={mapping.platform_stock > 0 ? "success" : "danger"}
                className="mt-1"
              >
                Tồn: {mapping.platform_stock || 0}
              </Badge>
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="text-muted" style={{ fontSize: "12px" }}>
              <Badge
                color={platform === "shopee" ? "warning" : "dark"}
                className="mr-2"
              >
                {platform === "shopee" ? "Shopee" : "TikTok"}
              </Badge>
              Shop: {mapping.shop_id}
            </div>
            <div className="text-muted" style={{ fontSize: "12px" }}>
              {mapping.product_id ? (
                <span className="text-success">
                  <i className="simple-icon-check mr-1" />
                  Đã liên kết
                </span>
              ) : (
                <span className="text-warning">
                  <i className="simple-icon-exclamation mr-1" />
                  Chưa liên kết
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const {
      activeTab,
      shopeeMappings,
      shopeeMeta,
      tiktokMappings,
      loadingShopee,
      loadingTiktok,
      shopeeError,
      searchQuery,
    } = this.state;

    const filteredTiktok = this.filterMappings(tiktokMappings);

    return (
      <>
        <Row>
          <Colxx xxs="12">
            <Breadcrumb heading="Sản phẩm sàn TMĐT" match={this.props.match} />
            <Separator className="mb-5" />
          </Colxx>
        </Row>

        <Row>
          <Colxx xxs="12">
            <Card className="mb-4">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="mb-1">Quản lý sản phẩm đa kênh</h5>
                    <p className="text-muted mb-0">
                      Xem và liên kết sản phẩm từ các sàn TMĐT với hệ thống
                    </p>
                  </div>
                  <div style={{ width: 300 }}>
                    <Input
                      type="text"
                      placeholder="Tìm theo tên, SKU..."
                      value={searchQuery}
                      onChange={this.handleSearchChange}
                    />
                  </div>
                </div>

                <Nav tabs className="separator-tabs mb-4">
                  <NavItem>
                    <NavLink
                      className={activeTab === "1" ? "active" : ""}
                      onClick={() => this.toggleTab("1")}
                      style={{ cursor: "pointer" }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          width: 18,
                          height: 18,
                          backgroundColor: "#EE4D2D",
                          borderRadius: 3,
                          textAlign: "center",
                          lineHeight: "18px",
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: 10,
                          marginRight: 8,
                        }}
                      >
                        S
                      </span>
                      Shopee ({shopeeMeta.total})
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => this.toggleTab("2")}
                      style={{ cursor: "pointer" }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          width: 18,
                          height: 18,
                          backgroundColor: "#000",
                          borderRadius: 3,
                          textAlign: "center",
                          lineHeight: "18px",
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: 10,
                          marginRight: 8,
                        }}
                      >
                        T
                      </span>
                      TikTok ({tiktokMappings.length})
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId="1">
                    {loadingShopee ? (
                      <p className="text-muted">Đang tải...</p>
                    ) : shopeeError ? (
                      <p className="text-danger">{shopeeError}</p>
                    ) : shopeeMappings.length === 0 ? (
                      <div className="text-center py-5 text-muted">
                        <i
                          className="simple-icon-basket"
                          style={{ fontSize: 48 }}
                        />
                        <p className="mt-3">Chưa có sản phẩm Shopee nào</p>
                        <p className="small">
                          Vào Cài đặt → Kết nối sàn TMĐT → Import sản phẩm
                        </p>
                      </div>
                    ) : (
                      <>
                        {shopeeMappings.map((m) =>
                          this.renderMappingCard(m, "shopee"),
                        )}
                        <div className="d-flex justify-content-between align-items-center mt-3">
                          <Button
                            color="outline-secondary"
                            size="sm"
                            disabled={shopeeMeta.page <= 1 || loadingShopee}
                            onClick={() =>
                              this.changeShopeePage(shopeeMeta.page - 1)
                            }
                          >
                            Trước
                          </Button>
                          <span className="text-muted small">
                            Trang {shopeeMeta.page}/{shopeeMeta.total_pages} ·{" "}
                            {shopeeMeta.total} sản phẩm
                          </span>
                          <Button
                            color="outline-secondary"
                            size="sm"
                            disabled={
                              shopeeMeta.page >= shopeeMeta.total_pages ||
                              loadingShopee
                            }
                            onClick={() =>
                              this.changeShopeePage(shopeeMeta.page + 1)
                            }
                          >
                            Sau
                          </Button>
                        </div>
                      </>
                    )}
                  </TabPane>
                  <TabPane tabId="2">
                    {loadingTiktok ? (
                      <p className="text-muted">Đang tải...</p>
                    ) : filteredTiktok.length === 0 ? (
                      <div className="text-center py-5 text-muted">
                        <i
                          className="simple-icon-basket"
                          style={{ fontSize: 48 }}
                        />
                        <p className="mt-3">Chưa có sản phẩm TikTok nào</p>
                        <p className="small">
                          Vào Cài đặt → Kết nối sàn TMĐT → Import sản phẩm
                        </p>
                      </div>
                    ) : (
                      filteredTiktok.map((m) =>
                        this.renderMappingCard(m, "tiktok"),
                      )
                    )}
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Colxx>
        </Row>
      </>
    );
  }
}

export default EcommerceProducts;
